<template>
  <div class="container mt-4">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center flex-wrap gap-3 bg-white p-3 rounded border mb-4">
      <div class="d-flex align-items-center gap-3 flex-wrap">
        <h4 class="mb-0 fw-semibold">{{ profile.name || 'Work Order' }}</h4>
        <span class="badge bg-light text-secondary fw-normal px-3 py-1">#{{ profile.external_id || 'N/A' }}</span>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary btn-sm" @click="$router.back()">
          <i class="fas fa-arrow-left"></i> Back
        </button>
      
      </div>
    </div>

    <!-- Loading -->
    <LoadingBar v-if="loading" message="Loading Work Order Details..." />

    <!-- Profile Content -->
    <div v-else class="bg-white rounded shadow-sm p-4">
      <div class="row">
        <!-- Left Column -->
        <div class="col-md-6">
          <div class="info-group">
            <div class="info-heading">Work Order Name</div>
            <div class="info-value">{{ profile.name || 'N/A' }}</div>
          </div>

          <div class="info-group">
            <div class="info-heading">Work Type</div>
            <div class="info-value">
              <span class="text-capitalize">{{ profile.work_type || 'N/A' }}</span>
            </div>
          </div>

          <div class="info-group">
            <div class="info-heading">Priority</div>
            <div class="info-value">
              <span :class="[getPriorityClass(profile.priority), 'text-capitalize']">
                {{ profile.priority || 'N/A' }}
              </span>
            </div>
          </div>

          <div class="info-group">
            <div class="info-heading">Frequency</div>
            <div class="info-value">
              <span class="text-capitalize">{{ profile.frequency || 'N/A' }}</span>
              <span class="text-muted" style="font-style:italic" v-if="profile.frequency!='custom'">(From: {{ profile.from_date }})</span>
            </div>
          </div>

          
        </div>

        <!-- Right Column -->
        <div class="col-md-6">
          <div class="info-group">
            <div class="info-heading">Location</div>
            <div class="info-value">{{ profile.get_location?.name || 'N/A' }}</div>
          </div>

          <div class="info-group">
            <div class="info-heading">Template</div>
            <div class="info-value">
              <router-link v-if="profile.get_template" class='text-primary' :to="`/template/builder/${profile.get_template.external_id}`">
                {{ profile.get_template.name }}
              </router-link>
            </div>
          </div>

          <div class="info-group">
            <div class="info-heading">Assign To</div>
            <div class="info-value">
              <span class="text-capitalize">{{ profile.assign_to || 'N/A' }}</span>
            </div>
          </div>

          <!-- Team -->
          <div class="info-group" v-if="profile.assign_to === 'team'">
            <div class="info-heading">Team</div>
            <div class="info-value">{{ profile.get_team?.name || 'N/A' }}</div>
          </div>

          <!-- Individual or Vendor -->
          <div class="info-group" v-if="profile.assign_to === 'individual' || profile.assign_to === 'vendor'">
            <div class="info-heading">{{ profile.assign_to === 'vendor' ? 'Vendor' : 'Assigned Users' }}</div>
            <div class="info-value">
              <span v-if="profile.get_users && profile.get_users.length > 0">
                <span v-for="(user, index) in profile.get_users" :key="user.id">
                  {{ user.first_name || '' }} {{ user.last_name || '' }} 
                  <span v-if="user.email" class="text-muted">({{ user.email }})</span>
                  <span v-if="index < profile.get_users.length - 1">, </span>
                </span>
              </span>
              <span v-else>N/A</span>
            </div>
          </div>

          <div class="info-group">
            <div class="info-heading">Created At</div>
            <div class="info-value">{{ formatDate(profile.created_at) }}</div>
          </div>
        </div>
      </div>

      <!-- Frequency Detail -->
      <div v-if="profile.frequency_detail" class="mt-3 pt-3 border-top">
        <div class="info-heading">Frequency Detail</div>
        <div class="info-value">
          <div v-if="profile.frequency === 'daily'">
            <span v-if="profile.frequency_detail.exclude && profile.frequency_detail.exclude.length > 0">
              Exclude: {{ profile.frequency_detail.exclude.join(', ') }}
            </span>
            <span v-else>No exclusions</span>
            <span v-if="profile.frequency_detail.times && profile.frequency_detail.times.length > 0" class="d-block">
              Times: {{ profile.frequency_detail.times.join(', ') }}
            </span>
          </div>
          <div v-else-if="profile.frequency === 'weekly'">
            <span>Days: {{ profile.frequency_detail.days?.join(', ') || 'N/A' }}</span>
            <span v-if="profile.frequency_detail.times && profile.frequency_detail.times.length > 0" class="d-block">
              Times: {{ profile.frequency_detail.times.join(', ') }}
            </span>
          </div>
          <div v-else-if="profile.frequency === 'monthly'">
            <span>Weeks: {{ profile.frequency_detail.weeks?.join(', ') || 'N/A' }}</span>
            <span class="d-block">Days: {{ profile.frequency_detail.days?.join(', ') || 'N/A' }}</span>
            <span v-if="profile.frequency_detail.times && profile.frequency_detail.times.length > 0" class="d-block">
              Times: {{ profile.frequency_detail.times.join(', ') }}
            </span>
          </div>
          <div v-else-if="profile.frequency === 'yearly'">
            <span>Months: {{ profile.frequency_detail.months?.join(', ') || 'N/A' }}</span>
            <span class="d-block">Weeks: {{ profile.frequency_detail.weeks?.join(', ') || 'N/A' }}</span>
            <span class="d-block">Days: {{ profile.frequency_detail.days?.join(', ') || 'N/A' }}</span>
            <span v-if="profile.frequency_detail.times && profile.frequency_detail.times.length > 0" class="d-block">
              Times: {{ profile.frequency_detail.times.join(', ') }}
            </span>
          </div>
          <div v-else-if="profile.frequency === 'custom'">
            <span>Schedule On: {{ profile.frequency_detail.schedule_date || 'N/A' }}</span>
            <span v-if="profile.frequency_detail.times && profile.frequency_detail.times.length > 0" class="d-block">
              Times: {{ profile.frequency_detail.times.join(', ') }}
            </span>
          </div>
        </div>
      </div>

      <!-- Assets -->
      <div v-if="profile.get_assets && profile.get_assets.length > 0" class="mt-3 pt-3 border-top">
        <div class="info-heading">Assets</div>
        <div class="info-value">
          <span v-for="(asset, index) in profile.get_assets" :key="index">
            {{ asset.name }}{{ index < profile.get_assets.length - 1 ? ', ' : '' }}
          </span>
        </div>
      </div>

      <!-- Categories -->
      <div v-if="profile.get_categories && profile.get_categories.length > 0" class="mt-3 pt-3 border-top">
        <div class="info-heading">Categories</div>
        <div class="info-value">
          <span v-for="(category, index) in profile.get_categories" :key="index">
            {{ category.name }}{{ index < profile.get_categories.length - 1 ? ', ' : '' }}
          </span>
        </div>
      </div>

      <!-- Requests -->
      <div v-if="profile.get_requests && profile.get_requests.length > 0" class="mt-3 pt-3 border-top">
        <div class="info-heading">Linked Requests</div>
        <div class="info-value">
          <div v-for="(request, index) in profile.get_requests" :key="index">
            <a href="#" class="text-primary" @click.prevent="viewRequest(request)">
              {{ request.title }}
            </a>
            <small class="text-muted ms-2">/{{ request.created_at }}</small>
            <small class="text-muted d-block"></small>
          </div>
        </div>
      </div>

      <!-- Description -->
      <div class="mt-3 pt-3 border-top">
        <div class="info-heading">Description</div>
        <div class="info-value" style="white-space: pre-wrap;">{{ profile.description || 'No description provided.' }}</div>
      </div>

      <!-- Attachments -->
      <div v-if="profile.files && profile.files.length > 0" class="mt-3 pt-3 border-top">
        <div class="info-heading">Attachments</div>
        <div class="d-flex flex-wrap gap-2 mt-2">
          <a v-for="(file, index) in profile.files" :key="index" :href="$backend_domain + file.path" target="_blank" class="attachment-link">
            <i class="fas fa-paperclip"></i> {{ file.name || `File ${index + 1}` }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import LoadingBar from '@/components/utils/LoadingBar.vue'

export default {
  name: 'WorkOrderProfile',
  components: {
    LoadingBar
  },
  data() {
    return {
      profile: {},
      loading: false
    }
  },
  mounted() {
    this.fetchProfile()
  },
  methods: {
    viewRequest(request) {
      this.$router.push(`/wo/request/profile/${request.external_id}`)
    },
    
    async fetchProfile() {
      this.loading = true

      const result = await form_api_submit({
        url: `wo/profile/${this.$route.params.id}`,
        method: 'GET',
        show_success_toast: false,
        show_error_toast: true
      })

      if (result.success) {
        this.profile = result.data
      }

      this.loading = false
    },

    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getPriorityClass(priority) {
      const classes = {
        low: 'badge bg-info',
        medium: 'badge bg-warning',
        high: 'badge bg-danger'
      }
      return classes[priority] || 'badge bg-secondary'
    },

    getStatusClass(status) {
      const classes = {
        pending: 'badge bg-warning',
        in_progress: 'badge bg-primary',
        completed: 'badge bg-success',
        rejected: 'badge bg-danger'
      }
      return classes[status?.toLowerCase()] || 'badge bg-secondary'
    }
  }
}
</script>

<style scoped>
.info-group {
  margin-bottom: 18px;
}

.info-heading {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.info-value {
  font-size: 14px;
  color: #1a202c;
  font-weight: 500;
}

.attachment-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  font-size: 13px;
  color: #0d6efd;
  text-decoration: none;
  transition: all 0.2s;
}

.attachment-link:hover {
  background: #e9ecef;
  text-decoration: none;
}
</style>